<template>
  <div :style="{ 'width': $vuetify.breakpoint.smAndDown ? 'auto' : '600px', 'margin': '16px auto'}">
    <manage-users-button v-if="isAdmin" />
    <v-card>
      <template v-if="loadingSettings === 0">
        <v-card-title primary-title style="display: block" class="pb-0">
          <h3 v-t="'settingsContainer.settings'" class="headline" />
          <span v-t="'settingsContainer.settingsSubtitle'" />
        </v-card-title>
        <v-card-text class="pb-0">
          <v-flex xs12>
            <v-select
              v-model="selectedLanguage"
              :items="languages"
              item-text="name"
              item-value="id"
              :label="$t('settingsContainer.preferredLanguage')"
              @change="saveSettings"
            />
            <v-select
              v-model="selectedCity"
              :items="cities"
              item-text="name"
              item-value="id"
              :label="$t('settingsContainer.city')"
              @change="saveSettings"
            />
            <v-layout v-if="isAdmin" row>
              <v-flex xs6>
                <v-text-field
                  ref="dailyStartHour"
                  v-model="dailyStartHour"
                  class="pr-3"
                  :label="$t('settingsContainer.dailyStartHour')"
                  required
                  :rules="[rules.required, rules.decimal]"
                  @input="saveDefaults(1500)"
                />
                <v-text-field
                  ref="dailyStartDay"
                  v-model="dailyStartDay"
                  class="pr-3"
                  :label="$t('settingsContainer.dailyStartDay')"
                  required
                  :rules="[rules.required, rules.integer]"
                  @input="saveDefaults(1500)"
                />
              </v-flex>
              <v-flex xs6>
                <v-text-field
                  ref="fridayStartHour"
                  v-model="fridayStartHour"
                  class="pl-3"
                  :label="$t('settingsContainer.fridayStartHour')"
                  required
                  :rules="[rules.required, rules.decimal]"
                  @input="saveDefaults(1500)"
                />
                <v-text-field
                  ref="fridayStartDay"
                  v-model="fridayStartDay"
                  class="pl-3"
                  :label="$t('settingsContainer.fridayStartDay')"
                  required
                  :rules="[rules.required, rules.integer]"
                  @input="saveDefaults(1500)"
                />
              </v-flex>
            </v-layout>
            <v-switch v-model="disableNotifications" color="primary" :label="$t('settingsContainer.disableFancyOrderNotifications')" @change="toggleNotifications()" />
          </v-flex>
        </v-card-text>
      </template>
      <template v-else>
        <div class="center" :style="{height: '33vh'}">
          <v-progress-circular
            class="vertical-center"
            indeterminate
            :width="6"
            :size="100"
            color="primary"
          />
        </div>
      </template>
    </v-card>
    <appearance-card />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import StorageService from '@/services/StorageService';
import ValidationService from '@/services/ValidationService';
import AuthenticationService from '@/services/AuthenticationService';
import AppearanceCard from './appearance-card/AppearanceCard.vue';
import ManageUsersButton from './ManageUsersButton.vue';

export default {
  name: 'SettingsContainer',
  components: {
    'appearance-card': AppearanceCard,
    'manage-users-button': ManageUsersButton,
  },
  data: () => ({
    selectedLanguage: 1,
    selectedCity: 1,
    dailyStartHour: undefined,
    dailyStartDay: undefined,
    fridayStartHour: undefined,
    fridayStartDay: undefined,
    loadingSettings: 0,
    dirty: false,
    disableNotifications: StorageService.get('disableFancyMessages') === 'true',
    saveTimeout: null,
    rules: {
      required: ValidationService.rules.required(),
      integer: ValidationService.rules.integer(),
      decimal: ValidationService.rules.decimal(),
    },
  }),
  computed: {
    ...mapGetters('userInfoStore', ['userData', 'languages', 'cities']),
    ...mapState('defaultValuesStore', ['dailyLunchStartDay', 'dailyLunchStartHour', 'fridayLunchStartDay', 'fridayLunchStartHour']),
    isAdmin() {
      return AuthenticationService.isAdmin();
    },
  },
  watch: {
    userData: {
      handler(newValue) {
        this.$i18n.locale = this.languages.find(l => l.id === newValue.languageId).locale;
        this.selectedLanguage = newValue.languageId;
        this.selectedCity = newValue.cityId;
      },
      deep: true,
    },
  },
  async mounted() {
    this.selectedLanguage = this.userData.languageId;
    this.selectedCity = this.userData.cityId;
    if (this.isAdmin) {
      await this.loadDefaultValues();
      this.dailyStartHour = this.dailyLunchStartHour;
      this.dailyStartDay = this.dailyLunchStartDay;
      this.fridayStartHour = this.fridayLunchStartHour;
      this.fridayStartDay = this.fridayLunchStartDay;
    }
  },
  methods: {
    ...mapActions('userInfoStore', ['updateUserData']),
    ...mapActions('defaultValuesStore', ['loadDefaultValues', 'updateDefaults']),
    ...mapActions('drawerStore', ['toggleDrawerLoading']),
    saveSettings(timeout = 100) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(async () => {
        this.toggleDrawerLoading(true);
        await this.updateUserData({
          languageId: this.selectedLanguage,
          cityId: this.selectedCity,
        });
        this.toggleDrawerLoading(false);
      }, timeout);
    },
    saveDefaults(timeout = 100) {
      clearTimeout(this.saveTimeout);
      if (this.$refs.dailyStartHour.validate()
        && this.$refs.dailyStartDay.validate()
        && this.$refs.fridayStartHour.validate()
        && this.$refs.fridayStartDay.validate()
      ) {
        this.saveTimeout = setTimeout(async () => {
          this.updateDefaults({
            dailyLunchStartDay: this.dailyStartDay,
            dailyLunchStartHour: this.dailyStartHour,
            fridayLunchStartDay: this.fridayStartDay,
            fridayLunchStartHour: this.fridayStartHour,
          });
        }, timeout);
      }
    },
    toggleNotifications() {
      StorageService.add('disableFancyMessages', this.disableNotifications);
    },
  },
};
</script>
